// @erp/ui/skeleton — THE skeleton family. One bone implementation for the whole
// repo (page-skeleton and data-table both compose these; nothing re-implements the
// shimmer).
//
// THE RULE (foundations §5): a skeleton MUST mirror the component it stands in for —
// same grid, same rounding (rounded-field for controls, rounded-500 for cards — the
// 8px card step, DEV-GUIDE-002 §12.1; rounded-box is the 16px dialog step),
// same heights and paddings. A skeleton that does not match its target is the bug,
// not a placeholder. Each family below names the component it mirrors so the two can
// be diffed by eye when either changes.
//
// Every class string is a COMPLETE static literal (picked from a map or a ternary
// between whole literals) — Tailwind must be able to see it, and nothing here
// assembles a class name at runtime.
import { For, Match, Show, Switch, type JSX } from 'solid-js';

const range = (n: number): number[] => Array.from({ length: n }, (_, i) => i);
const atLeast = (n: number | undefined, fallback: number) => Math.max(1, n ?? fallback);

/* ─────────────────────────── the primitive bone ─────────────────────────── */

/**
 * Rounding is a SHAPE, not a free class — a caller-supplied `rounded-*` would race
 * the base radius in the generated stylesheet, so the shape picks a whole literal.
 * Use `class` for size/spacing utilities only (`h-4 w-24`, `mt-1`, `shrink-0`).
 */
export type SkeletonShape =
  /** control-shaped (input, select, table cell text) — rounded-field */
  | 'bar'
  /** surface-shaped (card, tile, panel) — rounded-500, the card step (§12.1) */
  | 'box'
  /** button / avatar / badge — rounded-full */
  | 'pill'
  /** icon tile, mirrors StatCard's h-11 w-11 chip — rounded-700 */
  | 'chip'
  /** small chrome chip, mirrors EntityProfileHeader's fact chips — rounded-selector */
  | 'selector'
  /** the checkbox / radio box — 2px, the base.css CORNER SPLITS literal (radius = the 2px control stroke) */
  | 'control';

const SHAPE: Record<SkeletonShape, string> = {
  bar: 'animate-pulse bg-base-300 rounded-field',
  box: 'animate-pulse bg-base-300 rounded-500',
  pill: 'animate-pulse bg-base-300 rounded-full',
  chip: 'animate-pulse bg-base-300 rounded-700',
  selector: 'animate-pulse bg-base-300 rounded-selector',
  control: 'animate-pulse bg-base-300 rounded-[2px]',
};

// The same shapes WITHOUT the grey ground, for a tinted bone. A second map
// rather than `animate-pulse ${radius} ${ground}` assembled per call, because
// the default path must stay byte-identical: every existing bone still emits
// the exact string above, in that order.
const SHAPE_UNGROUNDED: Record<SkeletonShape, string> = {
  bar: 'animate-pulse rounded-field',
  box: 'animate-pulse rounded-500',
  pill: 'animate-pulse rounded-full',
  chip: 'animate-pulse rounded-700',
  selector: 'animate-pulse rounded-selector',
  control: 'animate-pulse rounded-[2px]',
};

export interface SkeletonProps {
  /** Size / spacing utilities only — the radius comes from `shape`. */
  class?: string;
  /** Which radius the bone wears — the shape of the thing it stands in for. Default `bar`. */
  shape?: SkeletonShape;
  /** Automation / anchor id on the bone. */
  id?: string;
  /**
   * A COMPLETE background utility that REPLACES the grey ground — and the only
   * colour a bone may ever carry.
   *
   * The rule (2026-09-22, the owner): a bone may be coloured exactly where the
   * real component is coloured in the same state — a status badge's two-tone
   * ground, a category chip's hue, a cover placeholder's hue. It comes from an
   * existing pair (`PAIR[hue].tint` in ../category-hue, `bg-<role>-tint`),
   * never a new colour and never a gradient, and the bone keeps its pulse.
   * Anything else stays `bg-base-300`.
   */
  tint?: string;
}

/** The primitive bone: `animate-pulse bg-base-300` (or `tint`), always aria-hidden. */
export function Skeleton(props: SkeletonProps): JSX.Element {
  return (
    <div
      id={props.id}
      aria-hidden="true"
      data-skeleton="bone"
      class={`${
        props.tint
          ? `${SHAPE_UNGROUNDED[props.shape ?? 'bar']} ${props.tint}`
          : SHAPE[props.shape ?? 'bar']
      } ${props.class ?? ''}`}
    />
  );
}

/* ─────────────────────────────── text block ─────────────────────────────── */

/** Mirrors a paragraph / description block. Last line runs short, as prose does. */
export function TextSkeleton(props: { lines?: number; class?: string; id?: string }): JSX.Element {
  const lines = () => atLeast(props.lines, 3);
  return (
    <div
      id={props.id}
      aria-hidden="true"
      data-skeleton="text"
      class={`flex flex-col gap-2 ${props.class ?? ''}`}
    >
      <For each={range(lines())}>
        {(i) => <Skeleton class={i === lines() - 1 ? 'h-3.5 w-2/3' : 'h-3.5 w-full'} />}
      </For>
    </div>
  );
}

/* ──────────────────────────────── stat row ──────────────────────────────── */

// Mirrors ../stat-card StatCard inside the dominant screen grid
// (`grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4`, 22 call sites):
// rounded-500 border border-base-300 bg-base-100 p-5 → label (text-sm) → value
// (text-2xl, mt-1) → h-11 w-11 rounded-700 icon chip.
const STAT_ROW_GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4';

export function StatRowSkeleton(props: { count?: number; id?: string }): JSX.Element {
  return (
    <div id={props.id} aria-hidden="true" data-skeleton="stat-row" class={STAT_ROW_GRID}>
      <For each={range(atLeast(props.count, 4))}>
        {() => (
          <div data-skeleton="stat" class="rounded-500 border border-base-300 bg-base-100 p-4">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <Skeleton class="h-4 w-24" />
                <Skeleton class="mt-1 h-7 w-20" />
              </div>
              {/* `bar`, the field step — NOT `chip` (that shape is the 10px
                  popover radius) and not `control` (2px, the checkbox): the
                  tile's icon chip is now the identity capsule's box, 32px at
                  corner-radius-500. */}
              <Skeleton class="size-8 shrink-0" />
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

/* ───────────────────────────────── table ────────────────────────────────── */

// Mirrors ../data-table DataTable: the same `overflow-x-auto rounded-500` wrapper,
// the same `table w-full` (+ `table-sm` when dense) so th/td padding comes from
// daisyUI exactly as it does for real rows, and the same `bg-(--ui-table-head-bg)`
// head tint. Cell widths cycle through complete literals so a loading table has the
// ragged silhouette of a real one.
const CELL_BAR = ['h-4 w-40', 'h-4 w-28', 'h-4 w-32', 'h-4 w-20', 'h-4 w-36', 'h-4 w-24'];
const CELL_BAR_DENSE = ['h-3 w-32', 'h-3 w-24', 'h-3 w-28', 'h-3 w-16', 'h-3 w-28', 'h-3 w-20'];
const HEAD_BAR = ['h-3 w-24', 'h-3 w-16', 'h-3 w-20', 'h-3 w-12', 'h-3 w-20', 'h-3 w-14'];

/* ── the column-aware cell (2026-09-22, the owner: "the skeleton … should match
   the row with its component exactly") ──────────────────────────────────────
   The rotating width list above gives a table the SILHOUETTE of a table; it
   cannot know that column 3 holds a badge and column 8 two icon buttons. A
   caller that knows its columns passes `cells`, and each bone then stands
   where its content stands, at the content's own size. Nothing here invents a
   size: every literal below names the component it mirrors. */

/**
 * What a column's BONE looks like while the row is loading — the shape of the
 * component that cell renders, greyed.
 */
export type SkeletonCellShape =
  /** free text — one bar, sized from the column's `width` when it has one, else the rotating list */
  | 'text'
  /** a title over a subtitle (two lines in one cell) — a bar at 80 % over a shorter, smaller one */
  | 'two-line'
  /** a StatusBadge — `badge badge-sm`: 20px tall, rounded-selector (daisyUI's badge radius) */
  | 'badge'
  /** a CategoryChip / Tag — CHIP_SIZE.s: 20px tall, rounded-selector, narrower than a badge */
  | 'chip'
  /** a figure (a count, a year, money) — a short bar, right-aligned where the column is */
  | 'number'
  /** a code set in the monospace face (an ISBN, a barcode, a reference) */
  | 'mono'
  /** a RowActions strip — `count` IconButton squares at the --ds-target box, justified to the end */
  | 'actions'
  /** an avatar / initials tile — a round bone at the 32px avatar box */
  | 'avatar'
  /** a cover / photo thumbnail — a rounded-field box at a book's 3:4 face (h-12 w-9) */
  | 'thumb'
  /** the leading selection cell — the 16px checkbox box (Checkbox size `s`) */
  | 'select'
  /** nothing at all — a cell whose content is chrome the skeleton must not imply */
  | 'none';

/** One rendered column's bone, in the order the columns render. */
export interface SkeletonCell {
  /** The bone's shape — what the real cell renders. */
  shape: SkeletonCellShape;
  /** The column's declared CSS width (`meta.width`). A `text` bone then takes 60 % of its cell instead of a list width, so it cannot outgrow its column. */
  width?: string;
  /** The column's alignment — pushes the bone to the end / centre, as the real cell's text goes. */
  align?: 'left' | 'right' | 'center';
  /**
   * A leading FIGURE inside the same cell, before the bone — for the cells
   * that put a portrait or a cover beside their text rather than in a column
   * of their own (a patron row's avatar + name/email, a card register's
   * cardholder). Without it the text bone starts at the cell's edge and every
   * word in the widest column jumps right by the figure's width the moment the
   * rows land — the same class of defect as the leading selection cell (audit
   * SK-02). `avatar` is the 32px round portrait; `thumb` the 3:4 cover face.
   * The bone that follows keeps its own shape and takes the rest of the cell.
   */
  lead?: 'avatar' | 'thumb';
  /**
   * A complete background utility the bone carries INSTEAD of the grey — only
   * where the real cell is coloured in the same state (a badge's two-tone
   * ground, a chip's hue). A LIST rotates by row, for a column whose real
   * chips are hue-hashed: a loading block then reads like the block it
   * becomes rather than a grey wall.
   */
  tint?: string | readonly string[];
  /** How many bones — an `actions` column's glyph count (default 2). */
  count?: number;
  /**
   * The real ../row-actions strip draws a hairline rule before its trailing
   * destructive action. That rule is 9px of the column's width (1px + `mx-0.5`
   * + the extra `gap-1` it opens), so a bone strip without it makes the PINNED
   * actions column — the most visible one on the screen — grow when the rows
   * land (audit SK-03). Position is free: wherever the divider sits, the strip
   * is the same width, so the bone draws it where the convention puts it.
   */
  divider?: boolean;
  /** Classes for the `<td>` itself: the column's hide-below, pin and ground rules, so a bone row hides and pins exactly where a real row does. */
  class?: string;
}

/** Bones are blocks, so alignment is a margin, not `text-align`. */
const ALIGN_BONE: Record<'left' | 'right' | 'center', string> = {
  left: '',
  right: 'ms-auto',
  center: 'mx-auto',
};

const ALIGN_STACK: Record<'left' | 'right' | 'center', string> = {
  left: 'items-start',
  right: 'items-end',
  center: 'items-center',
};

function CellBone(props: {
  cell: SkeletonCell;
  /** Column index — the rotating width list's cursor for an unsized `text` bone. */
  col: number;
  /** Row index — the cursor into a rotating `tint` list. */
  row: number;
  dense?: boolean;
}): JSX.Element {
  const cell = () => props.cell;
  const align = () => cell().align ?? 'left';
  const tint = () => {
    const t = cell().tint;
    if (t === undefined || typeof t === 'string') return t;
    return t.length === 0 ? undefined : t[props.row % t.length];
  };
  // An unsized text bone keeps TODAY's ragged list; a sized one takes 60 % of
  // its own column (min 3rem), which is what stops a 160px bone from widening
  // an 84px column and shifting the table when the rows land.
  const textBone = () =>
    cell().width
      ? `${props.dense ? 'h-3' : 'h-4'} w-3/5 min-w-12`
      : props.dense
        ? (CELL_BAR_DENSE[props.col % CELL_BAR_DENSE.length] ?? 'h-3 w-24')
        : (CELL_BAR[props.col % CELL_BAR.length] ?? 'h-4 w-28');
  // The cell's own bone, named so the `lead` wrapper below can place it beside
  // a figure without either branch reusing the other's DOM node.
  const Body = () => (
    <Switch fallback={<Skeleton class={`${textBone()} ${ALIGN_BONE[align()]}`.trim()} tint={tint()} />}>
      <Match when={cell().shape === 'none'}>{null}</Match>
      <Match when={cell().shape === 'two-line'}>
        {/* The title + subtitle cell: the same two lines, the second shorter
            and lighter, so a row WITH a subtitle is the height it will be. */}
        <div class={`flex flex-col gap-1 ${ALIGN_STACK[align()]}`}>
          <Skeleton class="h-3.5 w-4/5 min-w-16" tint={tint()} />
          <Skeleton class="h-3 w-1/2 min-w-12" />
        </div>
      </Match>
      <Match when={cell().shape === 'badge'}>
        {/* StatusBadge = daisyUI `badge badge-sm`: height 20px, radius --radius-selector. */}
        <Skeleton shape="selector" class={`h-5 w-20 ${ALIGN_BONE[align()]}`} tint={tint()} />
      </Match>
      <Match when={cell().shape === 'chip'}>
        {/* CategoryChip / Tag at CHIP_SIZE.s — the same 20px box, a shorter label. */}
        <Skeleton shape="selector" class={`h-5 w-16 ${ALIGN_BONE[align()]}`} tint={tint()} />
      </Match>
      <Match when={cell().shape === 'number'}>
        <Skeleton class={`${props.dense ? 'h-3 w-8' : 'h-4 w-10'} ${ALIGN_BONE[align()]}`} tint={tint()} />
      </Match>
      <Match when={cell().shape === 'mono'}>
        <Skeleton class={`${props.dense ? 'h-3 w-20' : 'h-4 w-24'} ${ALIGN_BONE[align()]}`} tint={tint()} />
      </Match>
      <Match when={cell().shape === 'actions'}>
        {/* RowActions' own strip: `flex items-center justify-end gap-1` of
            IconButtons — a rounded-500 SQUARE at the --ds-target hit floor
            (32px desktop / 40 touch; the 1.75rem fallback is the skin-less
            mount), never the circle the pre-2026-09-18 recipe wore. */}
        <div class="flex items-center justify-end gap-1">
          <For each={range(atLeast(cell().count, 2))}>
            {(i) => (
              <>
                {/* RowActions' own rule, byte for byte — chrome, not content,
                    so it is NOT a bone: it does not pulse and it is drawn at
                    full strength, because it is exactly what the strip will
                    still show once the row lands. Before the LAST glyph, where
                    the RowActions convention puts the destructive action. */}
                <Show when={cell().divider && i === atLeast(cell().count, 2) - 1}>
                  <div data-skeleton="divider" class="w-px h-4 bg-base-300 mx-0.5 shrink-0" />
                </Show>
                <Skeleton shape="box" class="size-[var(--ds-target,1.75rem)] shrink-0" tint={tint()} />
              </>
            )}
          </For>
        </div>
      </Match>
      <Match when={cell().shape === 'avatar'}>
        <Skeleton shape="pill" class={`size-8 shrink-0 ${ALIGN_BONE[align()]}`} tint={tint()} />
      </Match>
      <Match when={cell().shape === 'thumb'}>
        {/* A cover's 3:4 face at the table's own thumb size, rounded-field like the real frame. */}
        <Skeleton class={`h-12 w-9 shrink-0 ${ALIGN_BONE[align()]}`} tint={tint()} />
      </Match>
      <Match when={cell().shape === 'select'}>
        {/* Checkbox size `s` = 16px, radius 2px = its own stroke (base.css CORNER SPLITS). */}
        <Skeleton shape="control" class="size-4 shrink-0" />
      </Match>
    </Switch>
  );

  return (
    <Show when={cell().lead} fallback={<Body />}>
      {(lead) => (
        // The real cell's own row: `flex items-center gap-3`, the figure
        // reserved at its true size, the text taking what is left.
        <div class="flex min-w-0 items-center gap-3">
          <Show
            when={lead() === 'avatar'}
            fallback={<Skeleton class="h-12 w-9 shrink-0" />}
          >
            <Skeleton shape="pill" class="size-8 shrink-0" />
          </Show>
          <div class="min-w-0 flex-1">
            <Body />
          </div>
        </div>
      )}
    </Show>
  );
}

export interface TableSkeletonProps {
  /** Bone rows to draw (default 8 — one page-ish, so the body keeps its height). */
  rows?: number;
  /** Columns to draw (default 5). Ignored when `cells` says what each column holds. */
  cols?: number;
  /** Matches DataTable's `dense` (table-sm) so the row rhythm is identical. */
  dense?: boolean;
  /**
   * Emit bare `<tr>`s only — for use inside an existing `<tbody>` (this is how
   * DataTable renders its loading state, keeping its own thead and chrome).
   */
  bodyOnly?: boolean;
  /** Applies to the standalone wrapper; ignored in `bodyOnly` mode. */
  id?: string;
  /**
   * ONE ENTRY PER RENDERED COLUMN, in render order — the bones then match the
   * row's own components instead of a rotating width list. `cols` is ignored
   * when this is passed. DataTable derives it from its columns' `meta.skeleton`;
   * omitted, every cell keeps the list exactly as before.
   */
  cells?: SkeletonCell[];
  /**
   * How many rows already stand ABOVE these bones — the cursor a rotating
   * `tint` list starts from. An appended batch that starts back at 0 shows the
   * same three colours under every page, which reads as a repeat rather than a
   * continuation (audit SK-06). DataTable passes its own row count on the
   * `loadingMore` path; the first load leaves it at 0.
   */
  rowOffset?: number;
}

export function TableSkeleton(props: TableSkeletonProps): JSX.Element {
  const rows = () => atLeast(props.rows, 8);
  const cols = () => (props.cells ? Math.max(1, props.cells.length) : atLeast(props.cols, 5));

  const Body = () => (
    <For each={range(rows())}>
      {(r) => (
        <tr aria-hidden="true" data-skeleton="table-row">
          <Show
            when={props.cells}
            fallback={
              <For each={range(cols())}>
                {(c) => (
                  <td>
                    <Skeleton
                      class={
                        props.dense
                          ? (CELL_BAR_DENSE[c % CELL_BAR_DENSE.length] ?? 'h-3 w-24')
                          : (CELL_BAR[c % CELL_BAR.length] ?? 'h-4 w-28')
                      }
                    />
                  </td>
                )}
              </For>
            }
          >
            {(cells) => (
              <For each={cells()}>
                {(cell, c) => (
                  // `data-select` is how the selection cell says it carries its
                  // OWN box: the table's bone-cell padding sync steps aside for
                  // it, because the real selection cell is not on the generic
                  // cell padding either (audit SK-02).
                  <td class={cell.class} data-select={cell.shape === 'select' ? '' : undefined}>
                    <CellBone
                      cell={cell}
                      /* THE ROTATION COUNTS COLUMNS, NOT CELLS. The selection
                         cell is chrome, not a column: letting it consume
                         index 0 would shift every undeclared table's bone
                         widths by one the day the leading cell started being
                         derived (2026-09-22). */
                      col={cells().some((x) => x.shape === 'select') ? Math.max(0, c() - 1) : c()}
                      row={(props.rowOffset ?? 0) + r}
                      dense={props.dense}
                    />
                  </td>
                )}
              </For>
            )}
          </Show>
        </tr>
      )}
    </For>
  );

  return (
    <Show
      when={props.bodyOnly}
      fallback={
        <div id={props.id} data-skeleton="table" class="overflow-x-auto rounded-500">
          <table class={props.dense ? 'table w-full table-sm' : 'table w-full'}>
            <thead>
              <tr class="bg-(--ui-table-head-bg)" aria-hidden="true">
                <For each={range(cols())}>
                  {(c) => (
                    <th scope="col">
                      <Skeleton class={HEAD_BAR[c % HEAD_BAR.length] ?? 'h-3 w-20'} />
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <Body />
            </tbody>
          </table>
        </div>
      }
    >
      <Body />
    </Show>
  );
}

/* ───────────────────────────────── form ─────────────────────────────────── */

// Mirrors ../form-section FormSection inside ../form-page FormPage's
// `flex flex-col gap-6` stack: `card card-border border-base-300 bg-base-100`,
// header `border-b border-base-300 px-5 py-3.5`, body `grid gap-4 p-5` on the same
// responsive column map. A field bone = fieldset-legend (h-3) + control (h-10,
// daisyUI's --size-field input height), rounded-field like the real input.
const FORM_SECTION_COLS: Record<1 | 2 | 3, string> = {
  1: 'grid gap-4 p-5 grid-cols-1',
  2: 'grid gap-4 p-5 grid-cols-1 sm:grid-cols-2',
  3: 'grid gap-4 p-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
};

/** One legend + control pair — the shape of a `<Field><TextInput/></Field>`. */
function FieldBone() {
  return (
    <div data-skeleton="field" class="flex flex-col gap-1.5">
      <Skeleton class="h-3 w-24" />
      <Skeleton class="h-10 w-full" />
    </div>
  );
}

export function FormSkeleton(props: {
  sections?: number;
  fieldsPerSection?: number;
  /** FormSection's `columns` (default 2 — the dominant layout on real forms). */
  columns?: 1 | 2 | 3;
  id?: string;
}): JSX.Element {
  return (
    <div id={props.id} aria-hidden="true" data-skeleton="form" class="flex flex-col gap-6">
      <For each={range(atLeast(props.sections, 2))}>
        {() => (
          <section
            data-skeleton="form-section"
            class="card card-border border-base-300 bg-base-100"
          >
            <header class="flex items-center justify-between gap-3 border-b border-base-300 px-5 py-3.5">
              <div class="flex min-w-0 flex-col gap-1.5">
                <Skeleton class="h-4 w-40" />
                <Skeleton class="h-3 w-64 max-w-full" />
              </div>
            </header>
            <div class={FORM_SECTION_COLS[props.columns ?? 2]}>
              <For each={range(atLeast(props.fieldsPerSection, 4))}>{() => <FieldBone />}</For>
            </div>
          </section>
        )}
      </For>
    </div>
  );
}

/* ───────────────────────────────── card ─────────────────────────────────── */

// Mirrors ../card Card + CardHeader: rounded-500 border border-base-300 bg-base-100,
// header `border-b border-base-300 px-5 py-4` (h3 + subtitle + actions), body p-5.
export function CardSkeleton(props: {
  lines?: number;
  /** Include the CardHeader row (default true). */
  header?: boolean;
  class?: string;
  id?: string;
}): JSX.Element {
  return (
    <div
      id={props.id}
      aria-hidden="true"
      data-skeleton="card"
      class={`rounded-500 border border-base-300 bg-base-100 ${props.class ?? ''}`}
    >
      <Show when={props.header ?? true}>
        <div class="flex items-start justify-between gap-4 border-b border-base-300 px-5 py-4">
          <div class="flex min-w-0 flex-col gap-2">
            <Skeleton class="h-5 w-40" />
            <Skeleton class="h-3.5 w-56 max-w-full" />
          </div>
          <Skeleton shape="pill" class="h-8 w-20 shrink-0" />
        </div>
      </Show>
      <div class="p-5">
        <TextSkeleton lines={props.lines ?? 3} />
      </div>
    </div>
  );
}

/* ──────────────────────────────── detail ────────────────────────────────── */

// Mirrors a detail screen: ../entity-profile-header EntityProfileHeader
// (Card p-5 → avatar · h1 + badges · subtitle · rounded-selector fact chips · actions)
// → a Card whose body is a ../definition-grid DefinitionGrid
// (`grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2`, dt then dd mt-1) → a card stack.
export function DetailSkeleton(props: {
  /** DefinitionGrid rows (default 6). */
  facts?: number;
  /** Cards below the fact grid (default 2). */
  cards?: number;
  id?: string;
}): JSX.Element {
  return (
    <div id={props.id} aria-hidden="true" data-skeleton="detail" class="flex flex-col gap-5">
      <div data-skeleton="detail-header" class="rounded-500 border border-base-300 bg-base-100 p-5">
        <div class="flex flex-wrap items-start gap-4">
          <Skeleton shape="pill" class="h-14 w-14 shrink-0" />
          <div class="min-w-0 flex-1">
            <Skeleton class="h-6 w-56 max-w-full" />
            <Skeleton class="mt-1.5 h-4 w-72 max-w-full" />
            <div class="mt-3 flex flex-wrap items-center gap-1.5">
              <For each={range(3)}>{() => <Skeleton shape="selector" class="h-6 w-28" />}</For>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-2">
            <Skeleton shape="pill" class="h-8 w-24" />
            <Skeleton shape="pill" class="h-8 w-20" />
          </div>
        </div>
      </div>

      <div class="rounded-500 border border-base-300 bg-base-100">
        <div class="flex items-start justify-between gap-4 border-b border-base-300 px-5 py-4">
          <Skeleton class="h-5 w-40" />
        </div>
        <div class="p-5">
          <div
            data-skeleton="definition-grid"
            class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2"
          >
            <For each={range(atLeast(props.facts, 6))}>
              {() => (
                <div data-skeleton="definition-item">
                  <Skeleton class="h-3 w-24" />
                  <Skeleton class="mt-1 h-4 w-36 max-w-full" />
                </div>
              )}
            </For>
          </div>
        </div>
      </div>

      <For each={range(atLeast(props.cards, 2))}>{() => <CardSkeleton />}</For>
    </div>
  );
}

/* ──────────────────────────────── panel ─────────────────────────────────── */

/**
 * Body of a ../side-panel SidePanel (or a Modal). Both already apply `p-5` to their
 * body slot, so this carries NO padding of its own — re-adding it is the
 * double-padding bug (foundations §7B).
 */
export function PanelSkeleton(props: { fields?: number; id?: string }): JSX.Element {
  return (
    <div id={props.id} aria-hidden="true" data-skeleton="panel" class="flex flex-col gap-4">
      <For each={range(atLeast(props.fields, 5))}>{() => <FieldBone />}</For>
    </div>
  );
}
